import { useState, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
  Paper,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { SavedBankAccount, stripeInputStyles, stripeLabelStyles } from '../hub-utils';
import PortalLoginEmailField from './portal-login-email-field';
import GuarantorAddressFields, { AddressDetails } from './guarantor-address-fields';

interface BillingDetails {
  firstName: string;
  lastName: string;
  email: string;
}

export interface BankAccountDetails {
  routingNumber: string;
  accountNumber: string;
}

interface PaymentHubACHFormProps {
  isActive: boolean;
  billingDetails: BillingDetails;
  onBillingDetailsChange: (field: keyof BillingDetails, value: string) => void;
  showNameFields?: boolean;
  savedBankAccounts?: SavedBankAccount[];
  onSavedMethodSelect?: (methodId: number | null) => void;
  selectedSavedMethodId?: number | null;
  onDeleteSavedMethod?: (methodId: number) => Promise<void>;
  deletingMethodId?: number | null;
  showAddressFields?: boolean;
  addressDetails?: AddressDetails;
  onAddressDetailsChange?: (field: keyof AddressDetails, value: string) => void;
  showEmailField?: boolean;
}

export interface ACHPaymentRef {
  getBankDetails: () => BankAccountDetails;
  getBillingDetails: () => BillingDetails;
  validate: () => boolean;
  getSelectedSavedMethodId: () => number | null;
  isUsingSavedMethod: () => boolean;
  isReady: boolean;
}

const PaymentHubACHForm = forwardRef<ACHPaymentRef, PaymentHubACHFormProps>(
  (
    {
      isActive,
      billingDetails,
      onBillingDetailsChange,
      showNameFields = false,
      savedBankAccounts = [],
      onSavedMethodSelect,
      selectedSavedMethodId,
      onDeleteSavedMethod,
      deletingMethodId,
      showAddressFields = false,
      addressDetails,
      onAddressDetailsChange,
      showEmailField = true,
    },
    ref
  ) => {

    const [bankDetails, setBankDetails] = useState<BankAccountDetails>({
      routingNumber: '',
      accountNumber: '',
    });

    const [useNewMethod, setUseNewMethod] = useState(savedBankAccounts.length === 0);
    const [errors, setErrors] = useState<{ routing?: string; account?: string }>({});
    const [touched, setTouched] = useState({ firstName: false, lastName: false, email: false });

    const handleFieldBlur = (field: 'firstName' | 'lastName' | 'email') => {
      setTouched(prev => ({ ...prev, [field]: true }));
    };

    const getFieldError = (field: 'firstName' | 'lastName' | 'email'): string | null => {
      if (!showNameFields && field !== 'email') return null;
      if (!touched[field]) return null;
      if (!billingDetails[field]?.trim()) {
        const labels = { firstName: 'First name', lastName: 'Last name', email: 'Email' };
        return `${labels[field]} is required`;
      }
      if (field === 'email' && billingDetails.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billingDetails.email)) {
        return 'Please enter a valid email address';
      }
      return null;
    };

    const getFieldInputStyle = (field: 'firstName' | 'lastName' | 'email') => ({
      ...stripeInputStyles,
      borderColor: getFieldError(field) ? '#df1b41' : '#e6e6e6',
    });

    // Effective selected method ID
    const effectiveSelectedMethodId = useNewMethod ? null : selectedSavedMethodId;

    // Validate bank details
    const validate = (): boolean => {
      if (!useNewMethod && effectiveSelectedMethodId) {
        return true;
      }

      const newErrors: { routing?: string; account?: string } = {};

      if (!bankDetails.routingNumber || bankDetails.routingNumber.length !== 9) {
        newErrors.routing = 'Routing number must be 9 digits';
      }

      if (!bankDetails.accountNumber || bankDetails.accountNumber.length < 4) {
        newErrors.account = 'Please enter a valid account number';
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    // Check if form is ready
    const isReady = useNewMethod
      ? bankDetails.routingNumber.length === 9 && bankDetails.accountNumber.length >= 4
      : effectiveSelectedMethodId !== null;

    useImperativeHandle(ref, () => ({
      getBankDetails: () => bankDetails,
      getBillingDetails: () => billingDetails,
      validate,
      getSelectedSavedMethodId: () => effectiveSelectedMethodId ?? null,
      isUsingSavedMethod: () => !useNewMethod && effectiveSelectedMethodId !== null,
      isReady,
    }));

    // Handle method selection change
    const handleMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      if (value === 'new') {
        setUseNewMethod(true);
        onSavedMethodSelect?.(null);
      } else {
        setUseNewMethod(false);
        const methodId = parseInt(value, 10);
        onSavedMethodSelect?.(methodId);
      }
    };

    // Handle bank detail changes
    const handleBankDetailChange = (field: keyof BankAccountDetails, value: string) => {
      const cleanValue = value.replace(/\D/g, '');
      setBankDetails(prev => ({ ...prev, [field]: cleanValue }));
      if (errors[field === 'routingNumber' ? 'routing' : 'account']) {
        setErrors(prev => ({ ...prev, [field === 'routingNumber' ? 'routing' : 'account']: undefined }));
      }
    };

    if (!isActive) {
      return null;
    }

    return (
      <Box test-id="payment-hub-ach-form">
        {/* Saved Bank Accounts Section */}
        {savedBankAccounts.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#f8f9fa' }} test-id="ach-saved-accounts-section">
            <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
              Select Bank Account
            </Typography>
            <RadioGroup
              value={useNewMethod ? 'new' : (effectiveSelectedMethodId?.toString() || '')}
              onChange={handleMethodChange}
              test-id="ach-saved-accounts-radiogroup"
            >
              {savedBankAccounts.map((account) => {
                const isSelected = effectiveSelectedMethodId === account.userPaymentMethodId && !useNewMethod;
                return (
                  <Paper
                    key={account.userPaymentMethodId}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      mb: 1,
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#e3f2fd' : 'white',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      '&:hover': { backgroundColor: '#f5f5f5' },
                    }}
                    onClick={() => {
                      setUseNewMethod(false);
                      onSavedMethodSelect?.(account.userPaymentMethodId);
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <FormControlLabel
                        value={account.userPaymentMethodId.toString()}
                        control={<Radio size="small" />}
                        label={
                          <Box display="flex" alignItems="center" gap={1}>
                            <AccountBalanceIcon fontSize="small" color="action" />
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {(account.bankName || 'Bank Account').toUpperCase() === 'ACH' ? 'ACH' : account.bankName || 'Bank Account'} •••• {account.last4}
                                {account.isDefault && (
                                  <Chip 
                                    label="Default" 
                                    size="small" 
                                    sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} 
                                  />
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        }
                        sx={{ flex: 1, m: 0 }}
                      />
                      {/* TODO: Re-enable delete button when ready
                      {onDeleteSavedMethod && (
                        <Tooltip title="Remove payment method">
                          <Box
                            component="span"
                            onClick={async (e: React.MouseEvent) => {
                              e.stopPropagation();
                              if (deletingMethodId) return;
                              await onDeleteSavedMethod(account.userPaymentMethodId);
                              if (effectiveSelectedMethodId === account.userPaymentMethodId) {
                                setUseNewMethod(true);
                                onSavedMethodSelect?.(null);
                              }
                            }}
                            sx={{
                              cursor: deletingMethodId ? 'default' : 'pointer',
                              p: 0.5,
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              '&:hover': { backgroundColor: deletingMethodId ? 'transparent' : 'rgba(0,0,0,0.04)' },
                            }}
                          >
                            {deletingMethodId === account.userPaymentMethodId ? (
                              <CircularProgress size={18} />
                            ) : (
                              <DeleteOutlineIcon fontSize="small" color="action" />
                            )}
                          </Box>
                        </Tooltip>
                      )}
                      */}
                    </Box>
                  </Paper>
                );
              })}
              {/* Add New Bank Account Option */}
              <Paper
                variant="outlined"
                test-id="ach-add-new-account"
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  backgroundColor: useNewMethod ? '#e3f2fd' : 'white',
                  borderColor: useNewMethod ? 'primary.main' : 'divider',
                  borderStyle: 'dashed',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                }}
                onClick={() => {
                  setUseNewMethod(true);
                  onSavedMethodSelect?.(null);
                }}
              >
                <FormControlLabel
                  value="new"
                  control={<Radio size="small" />}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <AddIcon fontSize="small" color="primary" />
                      <Typography variant="body2" color="primary">
                        Add new bank account
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0 }}
                />
              </Paper>
            </RadioGroup>
          </Paper>
        )}

        {/* New Bank Account Form */}
        {(useNewMethod || savedBankAccounts.length === 0) && (
          <Box
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              p: 2,
              backgroundColor: '#fafafa',
            }}
          >
            {/* Name Fields (if needed) */}
            {showNameFields && (
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography component="label" sx={stripeLabelStyles}>
                    First Name
                  </Typography>
                  <input
                    type="text"
                    value={billingDetails.firstName}
                    onChange={(e) => onBillingDetailsChange('firstName', e.target.value)}
                    onBlur={() => handleFieldBlur('firstName')}
                    placeholder="First name"
                    style={getFieldInputStyle('firstName')}
                    test-id="ach-first-name"
                  />
                  {getFieldError('firstName') && (
                    <Typography sx={{ color: '#df1b41', fontSize: '12px', mt: 0.5 }}>
                      {getFieldError('firstName')}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography component="label" sx={stripeLabelStyles}>
                    Last Name
                  </Typography>
                  <input
                    type="text"
                    value={billingDetails.lastName}
                    onChange={(e) => onBillingDetailsChange('lastName', e.target.value)}
                    onBlur={() => handleFieldBlur('lastName')}
                    placeholder="Last name"
                    style={getFieldInputStyle('lastName')}
                    test-id="ach-last-name"
                  />
                  {getFieldError('lastName') && (
                    <Typography sx={{ color: '#df1b41', fontSize: '12px', mt: 0.5 }}>
                      {getFieldError('lastName')}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {showEmailField && (
              <Box sx={{ mb: 2 }}>
                <PortalLoginEmailField
                  email={billingDetails.email}
                  onChange={(value) => onBillingDetailsChange('email', value)}
                  onBlur={() => handleFieldBlur('email')}
                  error={getFieldError('email')}
                  inputStyle={getFieldInputStyle('email')}
                  testId="ach-email"
                />
              </Box>
            )}

            {/* Address Fields (for guarantor) */}
            {showAddressFields && addressDetails && onAddressDetailsChange && (
              <GuarantorAddressFields
                addressDetails={addressDetails}
                onAddressDetailsChange={onAddressDetailsChange}
                testIdPrefix="ach"
              />
            )}

            {/* Bank Account Fields */}
            <Box sx={{ mb: 2 }}>
              <Typography component="label" sx={stripeLabelStyles}>
                Routing Number
              </Typography>
              <input
                type="text"
                value={bankDetails.routingNumber}
                onChange={(e) => handleBankDetailChange('routingNumber', e.target.value)}
                placeholder="110000000"
                maxLength={9}
                test-id="ach-routing-number"
                style={{
                  ...stripeInputStyles,
                  borderColor: errors.routing ? '#df1b41' : '#e6e6e6',
                }}
              />
              {errors.routing && (
                <Typography sx={{ color: '#df1b41', fontSize: '12px', mt: 0.5 }}>
                  {errors.routing}
                </Typography>
              )}
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography component="label" sx={stripeLabelStyles}>
                Account Number
              </Typography>
              <input
                type="text"
                value={bankDetails.accountNumber}
                onChange={(e) => handleBankDetailChange('accountNumber', e.target.value)}
                placeholder="000123456789"
                test-id="ach-account-number"
                style={{
                  ...stripeInputStyles,
                  borderColor: errors.account ? '#df1b41' : '#e6e6e6',
                }}
              />
              {errors.account && (
                <Typography sx={{ color: '#df1b41', fontSize: '12px', mt: 0.5 }}>
                  {errors.account}
                </Typography>
              )}
            </Box>

            {/* ACH Authorization Notice - matching Stripe Payment Element style */}
            <p 
              style={{ 
                fontSize: '12px', 
                color: '#757680', 
                lineHeight: '16px',
                letterSpacing: '-0.01em',
                marginTop: '16px',
                marginBottom: 0,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
                fontWeight: 400,
              }}
            >
              By providing your bank account information, you allow Finpay LLC to debit your bank account for future payments in accordance with their terms.
            </p>
          </Box>
        )}
      </Box>
    );
  }
);

PaymentHubACHForm.displayName = 'PaymentHubACHForm';

export default PaymentHubACHForm;
